import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Popover,
  TextField,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  RadioButtonUnchecked as PendingIcon,
  CheckCircle as DoneIcon,
  Add as AddIcon,
  Block as SkipIcon,
  Replay as UndoIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, parseISO } from 'date-fns';
import { treatmentSheetApi } from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import { treatmentSheetControls } from '../../auth/pageControls';
import { useAuth } from '../../auth/useAuth';
import type {
  AdministrationGridResponse,
  OrderWithSlots,
  AdministrationSlot,
  TreatmentCategory,
} from '../../types/treatmentSheet';
import ControlledSubstanceAdminDialog from '../DEALogs/ControlledSubstanceAdminDialog';
import { buildUnionColumns, isLegacyContinuous, slotForColumn } from './gridDays';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_ORDER: TreatmentCategory[] = [
  'FLUID',
  'MEDICATION',
  'PROCEDURE',
  'MONITORING',
  'NUTRITION',
  'OTHER',
];

const CATEGORY_COLOR: Record<TreatmentCategory, string> = {
  FLUID: '#1976d2',      // blue
  MEDICATION: '#7b1fa2', // purple
  PROCEDURE: '#f57c00',  // amber
  MONITORING: '#757575', // grey
  NUTRITION: '#388e3c',  // green
  OTHER: '#546e7a',      // blue-grey
};

const CATEGORY_LABEL: Record<TreatmentCategory, string> = {
  FLUID: 'Fluid',
  MEDICATION: 'Medication',
  PROCEDURE: 'Procedure',
  MONITORING: 'Monitoring',
  NUTRITION: 'Nutrition',
  OTHER: 'Other',
};

const FREQUENCY_LABEL: Record<string, string> = {
  Q1H: 'q1h', Q2H: 'q2h', Q4H: 'q4h', Q6H: 'q6h', Q8H: 'q8h', Q12H: 'q12h',
  SID: 'SID', BID: 'BID', TID: 'TID', QID: 'QID', PRN: 'PRN', CRI: 'CRI', ONCE: 'Once',
};

const ROW_HEADER_WIDTH = 210;
const CELL_WIDTH = 72;
const ADMIN_NOW_COL_WIDTH = 64;

// ─── Overdue pulse animation ──────────────────────────────────────────────────

const pulseKeyframe = `
  @keyframes pulse-red {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
`;

// ─── AdminPopover ─────────────────────────────────────────────────────────────

interface AdminPopoverProps {
  anchorEl: HTMLElement | null;
  orderId: string;
  orderName: string;
  slot: AdministrationSlot;
  onClose: () => void;
  sheetId: string;
  /** EXACT day-keyed query key — get/setQueryData don't prefix-match. */
  gridKey: readonly unknown[];
}

const AdminPopover: React.FC<AdminPopoverProps> = ({
  anchorEl,
  orderId,
  orderName,
  slot,
  onClose,
  sheetId,
  gridKey,
}) => {
  const queryClient = useQueryClient();
  const [actualDose, setActualDose] = useState(slot.actualDose ?? '');
  const [notes, setNotes] = useState('');

  const mutation = useMutation(
    () =>
      treatmentSheetApi.administerTreatment(orderId, {
        scheduledAt: slot.scheduledAt,
        actualDose: actualDose || undefined,
        notes: notes || undefined,
      }),
    {
      onMutate: async () => {
        // Optimistically update the day's grid (exact key — the page's
        // prefix invalidations still hit every day variant).
        const key = gridKey;
        await queryClient.cancelQueries(key);
        const prev = queryClient.getQueryData<AdministrationGridResponse>(key);
        if (prev) {
          queryClient.setQueryData<AdministrationGridResponse>(key, {
            ...prev,
            orders: prev.orders.map((o) =>
              o.id === orderId
                ? {
                    ...o,
                    slots: o.slots.map((s) =>
                      s.scheduledAt === slot.scheduledAt
                        ? { ...s, status: 'DONE', administeredAt: new Date().toISOString(), actualDose }
                        : s,
                    ),
                  }
                : o,
            ),
          });
        }
        return { prev };
      },
      onError: (_err, _vars, ctx: any) => {
        if (ctx?.prev) queryClient.setQueryData(gridKey, ctx.prev);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['admin-grid', sheetId]);
        // The stay's running total is only true if it moves with the grid.
        queryClient.invalidateQueries(['sheet-billing', sheetId]);
        onClose();
      },
    },
  );

  const scheduledLabel = format(parseISO(slot.scheduledAt), 'h:mm a, MMM d');

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: { p: 2, width: 280 } }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {orderName}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        Scheduled: {scheduledLabel}
      </Typography>

      <TextField
        label="Actual Dose (optional)"
        value={actualDose}
        onChange={(e) => setActualDose(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={2}
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        fullWidth
        onClick={() => mutation.mutate()}
        disabled={mutation.isLoading}
        startIcon={mutation.isLoading ? <CircularProgress size={14} /> : <DoneIcon />}
        color="success"
      >
        Confirm Administration
      </Button>
    </Popover>
  );
};

// ─── Grid Cell ────────────────────────────────────────────────────────────────

interface GridCellProps {
  slot: AdministrationSlot;
  order: OrderWithSlots;
  sheetId: string;
  gridKey: readonly unknown[];
  /** Past days render overdue slots neutrally (no red pulse) but clickable. */
  isToday: boolean;
  canAdminister: boolean;
  currentUserName?: string;
  currentUserId?: string;
}

const GridCell: React.FC<GridCellProps> = ({ slot, order, sheetId, gridKey, isToday, canAdminister, currentUserName, currentUserId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [csDialogOpen, setCsDialogOpen] = useState(false);
  const [cellHovered, setCellHovered] = useState(false);

  const isPRN = order.frequency === 'PRN';
  // PRN and CRI (continuous infusion) have no scheduled slots — both are charted
  // ad-hoc, so they use the "add a dose" affordance instead of a pending circle.
  const isAdHoc = order.frequency === 'PRN' || order.frequency === 'CRI';
  const isControlled = !!order.controlledSubstanceId && !!order.controlledSubstance;
  const openAdminUI = (e?: React.MouseEvent<HTMLElement>) => {
    if (isControlled) {
      setCsDialogOpen(true);
    } else if (e) {
      setAnchorEl(e.currentTarget as HTMLElement);
    }
  };
  const refreshGrid = () => {
    queryClient.invalidateQueries(['admin-grid', sheetId]);
    // The stay's running total is only true if it moves with the grid.
    queryClient.invalidateQueries(['sheet-billing', sheetId]);
  };

  // Undo mutation — reverts a DONE administration back to PENDING
  const undoMutation = useMutation(
    (_vars?: void) => treatmentSheetApi.updateAdministrationLog(slot.logId!, { status: 'PENDING' }),
    {
      onMutate: async () => {
        const key = gridKey;
        await queryClient.cancelQueries(key);
        const prev = queryClient.getQueryData<AdministrationGridResponse>(key);
        if (prev) {
          const now = new Date();
          const scheduledTime = new Date(slot.scheduledAt);
          const isOverdue = scheduledTime < now;
          queryClient.setQueryData<AdministrationGridResponse>(key, {
            ...prev,
            orders: prev.orders.map((o) =>
              o.id === order.id
                ? {
                    ...o,
                    slots: o.slots.map((s) =>
                      s.scheduledAt === slot.scheduledAt
                        ? { ...s, status: isOverdue ? 'OVERDUE' : 'PENDING', logId: null, administeredAt: null, administeredById: null, administeredByName: null, actualDose: null, notes: null }
                        : s,
                    ),
                  }
                : o,
            ),
          });
        }
        return { prev };
      },
      onError: (_err: any, _vars: any, ctx: any) => {
        if (ctx?.prev) queryClient.setQueryData(gridKey, ctx.prev);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['admin-grid', sheetId]);
        // The stay's running total is only true if it moves with the grid.
        queryClient.invalidateQueries(['sheet-billing', sheetId]);
      },
    },
  );

  // Quick-initial mutation — no popover, just records immediately
  const quickInitialMutation = useMutation(
    (_vars?: void) => treatmentSheetApi.administerTreatment(order.id, { scheduledAt: slot.scheduledAt }),
    {
      onMutate: async () => {
        const key = gridKey;
        await queryClient.cancelQueries(key);
        const prev = queryClient.getQueryData<AdministrationGridResponse>(key);
        if (prev) {
          queryClient.setQueryData<AdministrationGridResponse>(key, {
            ...prev,
            orders: prev.orders.map((o) =>
              o.id === order.id
                ? {
                    ...o,
                    slots: o.slots.map((s) =>
                      s.scheduledAt === slot.scheduledAt
                        ? { ...s, status: 'DONE', administeredAt: new Date().toISOString(), administeredByName: currentUserName ?? 'You' }
                        : s,
                    ),
                  }
                : o,
            ),
          });
        }
        return { prev };
      },
      onError: (_err: any, _vars: any, ctx: any) => {
        if (ctx?.prev) queryClient.setQueryData(gridKey, ctx.prev);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['admin-grid', sheetId]);
        // The stay's running total is only true if it moves with the grid.
        queryClient.invalidateQueries(['sheet-billing', sheetId]);
      },
    },
  );

  if (slot.status === 'DONE') {
    const initials = slot.administeredByName
      ? slot.administeredByName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
      : '??';
    const timeLabel = slot.administeredAt ? format(parseISO(slot.administeredAt), 'h:mm a') : '';

    return (
      <>
        <Box
          onMouseEnter={() => setCellHovered(true)}
          onMouseLeave={() => setCellHovered(false)}
          sx={{ width: CELL_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
        >
          {/* Undo button — appears on hover if canAdminister and logId exists */}
          {canAdminister && slot.logId && cellHovered && !undoMutation.isLoading ? (
            <Tooltip title="Undo — remove this administration">
              <IconButton
                size="small"
                onClick={() => undoMutation.mutate()}
                sx={{
                  p: 0.25,
                  color: 'error.main',
                  '&:hover': { bgcolor: 'error.50' },
                }}
              >
                <UndoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={
              isControlled
                ? `${slot.administeredByName ?? 'Unknown'} at ${timeLabel} — record signed (immutable)`
                : `${slot.administeredByName ?? 'Unknown'} at ${timeLabel} — click to add notes`
            }>
              <Box
                onClick={(e) => canAdminister && !isControlled && openAdminUI(e)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  cursor: canAdminister ? 'pointer' : 'default',
                  borderRadius: 1,
                }}
              >
                {undoMutation.isLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <>
                    <DoneIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, lineHeight: 1, fontWeight: 700 }}>
                      {initials}
                    </Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          )}
        </Box>
        {anchorEl && (
          <AdminPopover
            anchorEl={anchorEl}
            orderId={order.id}
            orderName={order.name}
            slot={slot}
            onClose={() => setAnchorEl(null)}
            sheetId={sheetId}
            gridKey={gridKey}
          />
        )}
      </>
    );
  }

  if (slot.status === 'SKIPPED') {
    return (
      <Tooltip title={slot.notes ?? 'Skipped'}>
        <Box sx={{ width: CELL_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled" variant="body2">—</Typography>
        </Box>
      </Tooltip>
    );
  }

  if (slot.status === 'OVERDUE') {
    return (
      <>
        {isToday && <style>{pulseKeyframe}</style>}
        <Box sx={{ width: CELL_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip
            title={
              !isToday
                ? 'Not recorded — click to backfill this dose'
                : isControlled
                  ? 'Overdue — open administration dialog'
                  : 'Overdue — click to initial'
            }
          >
            <IconButton
              size="small"
              disabled={!canAdminister || quickInitialMutation.isLoading}
              onClick={(e) => isControlled ? openAdminUI(e) : quickInitialMutation.mutate()}
              sx={isToday ? { animation: 'pulse-red 1.4s ease-in-out infinite', p: 0.5 } : { p: 0.5 }}
            >
              <PendingIcon
                sx={{ color: isToday ? theme.palette.error.main : theme.palette.action.active, fontSize: 20 }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        {anchorEl && !isControlled && (
          <AdminPopover anchorEl={anchorEl} orderId={order.id} orderName={order.name}
            slot={slot} onClose={() => setAnchorEl(null)} sheetId={sheetId} gridKey={gridKey} />
        )}
        {isControlled && order.controlledSubstance && (
          <ControlledSubstanceAdminDialog
            open={csDialogOpen} onClose={() => setCsDialogOpen(false)}
            onSuccess={refreshGrid}
            orderId={order.id} orderName={order.name}
            orderDose={order.dose} orderRoute={order.route}
            controlledSubstance={order.controlledSubstance}
            slot={slot}
            currentUserId={currentUserId ?? ''}
          />
        )}
      </>
    );
  }

  // PENDING (or PRN)
  return (
    <>
      <Box sx={{ width: CELL_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title={isControlled ? 'Click to administer (witness required)' : isPRN ? 'Administer PRN dose' : isAdHoc ? 'Record continuous-infusion dose' : 'Click to initial'}>
          <IconButton
            size="small"
            disabled={!canAdminister || quickInitialMutation.isLoading}
            onClick={(e) => {
              if (isControlled || isAdHoc) openAdminUI(e);
              else quickInitialMutation.mutate();
            }}
            sx={{ p: 0.5 }}
          >
            {quickInitialMutation.isLoading ? (
              <CircularProgress size={16} />
            ) : isControlled || isAdHoc ? (
              <AddIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            ) : (
              <PendingIcon sx={{ color: theme.palette.action.active, fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      {anchorEl && !isControlled && (
        <AdminPopover anchorEl={anchorEl} orderId={order.id} orderName={order.name}
          slot={slot} onClose={() => setAnchorEl(null)} sheetId={sheetId} gridKey={gridKey} />
      )}
      {isControlled && order.controlledSubstance && (
        <ControlledSubstanceAdminDialog
          open={csDialogOpen} onClose={() => setCsDialogOpen(false)}
          onSuccess={refreshGrid}
          orderId={order.id} orderName={order.name}
          orderDose={order.dose} orderRoute={order.route}
          controlledSubstance={order.controlledSubstance}
          slot={slot}
          currentUserId={currentUserId ?? ''}
        />
      )}
    </>
  );
};

// ─── Main TreatmentGrid ───────────────────────────────────────────────────────

interface TreatmentGridProps {
  sheetId: string;
  /** Selected ladder day ('yyyy-MM-dd') — part of the query key. */
  dayKey: string;
  windowStart?: Date;
  windowEnd?: Date;
  isToday: boolean;
  onEditOrder?: (order: OrderWithSlots) => void;
}

const TreatmentGrid: React.FC<TreatmentGridProps> = ({ sheetId, dayKey, windowStart, windowEnd, isToday, onEditOrder }) => {
  const theme = useTheme();
  const rbac = useRBAC();
  const { hasRole } = rbac;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  /**
   * TWO TERMS, AND-ed (INV-108 + decision #84). The PERMISSION is what every route here declares:
   * editing and discontinuing an order (treatment-order.controller.ts `update` :57-58, `remove`
   * :63-64) and recording a dose (administration-log.controller.ts `administer` :26-27) all want
   * `manage:medical_records`, so a person without it gets neither.
   *
   * ⚠ THE TWO ROLE TERMS ARE NOT THE SAME KIND OF RULE — corrected 2026-09-21.
   *
   *  - `isVet` MIRRORS the server. `treatment-order.service.ts:30` `CAN_ORDER = [VETERINARIAN,
   *    OWNER, ADMIN]` is enforced on create (:95), duplicate-day (:173), update (:356) and, since
   *    the no-access fix wave, discontinue (:384). It looked UI-only to every decorator walk
   *    because it lives in a SERVICE — which is exactly why the discontinue arm went ungated for
   *    so long, with this grid's `isVet` the only thing in front of it.
   *  - `canAdminister`'s technician arm IS UI-only: no route in the module gates dosing on a role,
   *    so the API would accept a dose from anyone holding the slug. Letting technicians dose while
   *    they may not order is this grid's clinical rule and the server does not know it.
   *
   * Collapsing either into the one slug would hand every technician order editing — a clinical
   * change nobody asked for. Opening it, or giving the split its own slug, is Ali's call (#84),
   * and it starts on the server, where the ordering half of the rule actually lives.
   */
  const can = treatmentSheetControls(rbac);
  const canWrite = can.orders;
  const isVet = canWrite && (hasRole('VETERINARIAN') || hasRole('OWNER') || hasRole('ADMIN'));
  // ⚠ `administrations`, NOT `orders`. Both flags carry `manage:medical_records` today, so the
  // two read alike — but they are different routes (administration-log.controller `administer`
  // vs treatment-order.controller `update`/`remove`) and the map names them separately for
  // exactly that reason. Dosing off the ORDERS flag meant the map's `administrations` entry had
  // no consumer at all, so re-pointing the dosing route's gate would have moved a flag nothing
  // read while this grid went on asking the order slug.
  const canAdminister =
    can.administrations &&
    (hasRole('VETERINARIAN') || hasRole('OWNER') || hasRole('ADMIN') || hasRole('VETERINARY_TECHNICIAN'));
  const currentUserName = user?.displayName ?? user?.email?.split('@')[0] ?? 'You';
  const currentUserId = user?.uid ?? '';

  // Day-keyed: the ladder's selected day IS the window. Only today's tab
  // polls — a 60s refetch on a history tab would just repaint the past.
  const gridKey = ['admin-grid', sheetId, dayKey] as const;
  const { data, isLoading, isError, refetch } = useQuery<AdministrationGridResponse>(
    gridKey,
    () =>
      treatmentSheetApi.getAdministrationGrid(
        sheetId,
        windowStart && windowEnd
          ? { windowStart: windowStart.toISOString(), windowEnd: windowEnd.toISOString() }
          : undefined,
      ),
    { refetchInterval: isToday ? 60000 : false, keepPreviousData: true },
  );

  const discontinueMutation = useMutation(
    (orderId: string) => treatmentSheetApi.discontinueOrder(orderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-grid', sheetId]);
        // The stay's running total is only true if it moves with the grid.
        queryClient.invalidateQueries(['sheet-billing', sheetId]);
      },
    },
  );

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (isError || !data) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="error">Failed to load treatment grid.</Typography>
        <Button size="small" onClick={() => refetch()} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Paper>
    );
  }

  // Sort orders by category
  const sortedOrders = [...data.orders].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.category);
    const bi = CATEGORY_ORDER.indexOf(b.category);
    if (ai !== bi) return ai - bi;
    return a.sortOrder - b.sortOrder;
  });

  // Column headers = sorted union of EVERY order's slots (the old
  // first-order derivation misaligned rows the moment schedules differed).
  const timeSlots: string[] = buildUnionColumns(sortedOrders);

  // The ladder's selected day is authoritative for the header label.
  const dateLabel = windowStart
    ? format(windowStart, 'EEE, MMM d, yyyy')
    : format(new Date(), 'EEE, MMM d, yyyy');

  // Current-time column highlight only makes sense on today's tab.
  const now = Date.now();
  const currentColIndex =
    isToday && timeSlots.length > 0
      ? timeSlots.reduce((bestIdx, ts, idx) => {
          const diff = Math.abs(parseISO(ts).getTime() - now);
          const bestDiff = Math.abs(parseISO(timeSlots[bestIdx]).getTime() - now);
          return diff < bestDiff ? idx : bestIdx;
        }, 0)
      : -1;

  return (
    <Paper sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            Treatment Orders
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dateLabel}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable grid */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <Box sx={{ minWidth: ROW_HEADER_WIDTH + timeSlots.length * CELL_WIDTH + (isToday ? ADMIN_NOW_COL_WIDTH : 0) + 16 }}>
          {/* Column headers */}
          <Box
            sx={{
              display: 'flex',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ width: ROW_HEADER_WIDTH, flexShrink: 0, px: 1.5, py: 0.75 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ORDER
              </Typography>
            </Box>
            {timeSlots.map((ts, colIdx) => {
              const dt = parseISO(ts);
              const label = format(dt, 'ha').toLowerCase(); // e.g. "6am"
              const isCurrent = colIdx === currentColIndex;
              return (
                <Box
                  key={ts}
                  sx={{
                    width: CELL_WIDTH,
                    flexShrink: 0,
                    py: 0.75,
                    textAlign: 'center',
                    borderLeft: isCurrent
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    bgcolor: isCurrent ? 'primary.50' : 'transparent',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={isCurrent ? 800 : 600}
                    color={isCurrent ? 'primary.main' : 'text.secondary'}
                  >
                    {label}
                  </Typography>
                  {isCurrent && (
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: '50%',
                      transform: 'translateX(-50%)',
                      width: 4, height: 4, borderRadius: '50%',
                      bgcolor: 'primary.main',
                    }} />
                  )}
                </Box>
              );
            })}
            {/* Sticky "Administer Now" column header — today only (an
                off-schedule dose recorded from a history tab would land on
                today anyway and confuse the day being viewed) */}
            {isToday && (
            <Box sx={{
              width: ADMIN_NOW_COL_WIDTH, flexShrink: 0, py: 0.75, textAlign: 'center',
              position: 'sticky', right: 0, zIndex: 3,
              borderLeft: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">NOW</Typography>
            </Box>
            )}
          </Box>

          {/* Order rows */}
          {sortedOrders.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {isToday
                  ? 'No orders for today yet. Add an order — or use "Duplicate to Today" to carry a previous day forward.'
                  : 'No orders were active on this day.'}
              </Typography>
            </Box>
          ) : (
            sortedOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                sheetId={sheetId}
                gridKey={gridKey}
                isToday={isToday}
                timeSlots={timeSlots}
                canAdminister={canAdminister}
                isVet={isVet}
                onDiscontinue={() => discontinueMutation.mutate(order.id)}
                onEdit={() => onEditOrder?.(order)}
                currentUserName={currentUserName}
                currentUserId={currentUserId}
                currentColIndex={currentColIndex}
              />
            ))
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── OrderRow ─────────────────────────────────────────────────────────────────

interface OrderRowProps {
  order: OrderWithSlots;
  sheetId: string;
  gridKey: readonly unknown[];
  isToday: boolean;
  /** Union column timestamps — rows render per-column (spacers where the
      order has no slot) so different schedules stay aligned. */
  timeSlots: string[];
  canAdminister: boolean;
  isVet: boolean;
  onDiscontinue: () => void;
  onEdit: () => void;
  currentUserName?: string;
  currentUserId?: string;
  currentColIndex: number;
}

const OrderRow: React.FC<OrderRowProps> = ({
  order,
  sheetId,
  gridKey,
  isToday,
  timeSlots,
  canAdminister,
  isVet,
  onDiscontinue,
  onEdit,
  currentUserName,
  currentUserId,
  currentColIndex,
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const catColor = CATEGORY_COLOR[order.category] ?? theme.palette.grey[500];

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      {/* Row header */}
      <Box
        sx={{
          width: ROW_HEADER_WIDTH,
          flexShrink: 0,
          px: 1.5,
          py: 0.5,
          borderLeft: `3px solid ${catColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.3, fontSize: 13 }}>
            {order.name}
          </Typography>
          {/* Dose/route/freq + category chip on one line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10, lineHeight: 1.2 }}>
              {[order.dose, order.route, FREQUENCY_LABEL[order.frequency]].filter(Boolean).join(' · ')}
            </Typography>
            <Chip
              label={CATEGORY_LABEL[order.category]}
              size="small"
              sx={{
                fontSize: 9,
                height: 14,
                flexShrink: 0,
                backgroundColor: catColor + '20',
                color: catColor,
                fontWeight: 600,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            {order.isActive === false ? (
              <Tooltip
                title={
                  (order as any).discontinuedAt
                    ? `Discontinued at ${format(parseISO((order as any).discontinuedAt), 'h:mm a')}`
                    : 'Discontinued'
                }
              >
                <Chip
                  label="Stopped"
                  size="small"
                  sx={{
                    fontSize: 9,
                    height: 14,
                    flexShrink: 0,
                    bgcolor: 'error.50',
                    color: 'error.main',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            ) : isLegacyContinuous(order) ? (
              <Tooltip title="Created before day-scoping — shown on its start day and days it was dosed; use Duplicate to carry it forward. As-needed/infusion (PRN/CRI) orders still show every day.">
                <Chip
                  label="Continuous"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 9, height: 14, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            ) : null}
          </Box>
        </Box>
        {/* Row actions — dedicated right slot, revealed on hover so they never
            overlap the order name (space is reserved to avoid a layout shift). */}
        {/* Past days are view + dose-backfill only: no editing of the
            historical record. Discontinue stays available off-today ONLY for
            legacy continuous rows (it's the same live order on every tab).
            Stopped rows get no actions at all. */}
        {isVet && order.isActive !== false && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              opacity: hovered ? 1 : 0,
              transition: 'opacity 120ms',
              pointerEvents: hovered ? 'auto' : 'none',
            }}
          >
            {isToday && (
              <Tooltip title="Edit order">
                <IconButton size="small" onClick={onEdit} sx={{ p: 0.25, color: 'primary.main' }}>
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {(isToday || isLegacyContinuous(order)) && (
              <Tooltip title="Discontinue order">
                <IconButton size="small" onClick={onDiscontinue} sx={{ p: 0.25, color: 'error.main' }}>
                  <SkipIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Cells — one per UNION column; spacer when this order has no slot
          there (rows used to render positionally, which misaligned mixed
          schedules) */}
      {order.slots.length === 0 && (order.frequency === 'PRN' || order.frequency === 'CRI') ? (
        <Box sx={{
          flex: 1, minHeight: 36, display: 'flex', alignItems: 'center',
          pl: 2, color: 'text.disabled', fontStyle: 'italic',
        }}>
          <Typography variant="caption">
            {order.frequency === 'CRI'
              ? isToday
                ? 'CRI — continuous infusion. Use "Administer Now" to chart rate checks →'
                : 'CRI — continuous infusion. No rate checks charted this day.'
              : isToday
                ? 'PRN — no doses recorded yet. Use "Administer Now" →'
                : 'PRN — no doses recorded this day.'}
          </Typography>
        </Box>
      ) : (
        timeSlots.map((ts, idx) => {
          const isCurrent = idx === currentColIndex;
          const slot = slotForColumn(order, ts);
          return (
          <Box
            key={ts}
            sx={{
              width: CELL_WIDTH,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderLeft: isCurrent
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.divider}`,
              bgcolor: isCurrent ? 'primary.50' : 'transparent',
              py: 0.25,
              minHeight: 36,
            }}
          >
            {slot ? (
              <GridCell
                slot={slot}
                order={order}
                sheetId={sheetId}
                gridKey={gridKey}
                isToday={isToday}
                canAdminister={canAdminister && order.isActive !== false}
                currentUserName={currentUserName}
                currentUserId={currentUserId}
              />
            ) : null}
          </Box>
          );
        })
      )}

      {/* Sticky "Administer Now" cell — today only (an off-schedule dose
          books at now(), which belongs to today's tab) */}
      {isToday && (
      <Box sx={{
        width: ADMIN_NOW_COL_WIDTH, flexShrink: 0,
        position: 'sticky', right: 0, zIndex: 1,
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {order.isActive !== false && (
        <AdminNowCell
          order={order}
          sheetId={sheetId}
          gridKey={gridKey}
          canAdminister={canAdminister}
          currentUserId={currentUserId}
        />
        )}
      </Box>
      )}
    </Box>
  );
};

// ─── AdminNowCell: ad-hoc admin trigger pinned to right edge of every row ────

interface AdminNowCellProps {
  order: OrderWithSlots;
  sheetId: string;
  gridKey: readonly unknown[];
  canAdminister: boolean;
  currentUserId?: string;
}

const AdminNowCell: React.FC<AdminNowCellProps> = ({ order, sheetId, gridKey, canAdminister, currentUserId }) => {
  const queryClient = useQueryClient();
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [csDialogOpen, setCsDialogOpen] = useState(false);

  const isControlled = !!order.controlledSubstanceId && !!order.controlledSubstance;

  // Synthetic slot at "now" for both flows
  const nowSlot: AdministrationSlot = {
    logId: null,
    scheduledAt: new Date().toISOString(),
    status: 'PENDING',
    administeredAt: null,
    administeredById: null,
    administeredByName: null,
    actualDose: null,
    notes: null,
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!canAdminister) return;
    if (isControlled) setCsDialogOpen(true);
    else setPopoverAnchor(e.currentTarget);
  };

  return (
    <>
      <Tooltip title={canAdminister ? 'Record dose now (off-schedule)' : 'Insufficient permissions'}>
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={!canAdminister}
            sx={{
              border: '1px dashed',
              borderColor: 'primary.light',
              bgcolor: 'primary.50',
              color: 'primary.main',
              p: 0.5,
              '&:hover': { bgcolor: 'primary.100' },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      {popoverAnchor && (
        <AdminPopover
          anchorEl={popoverAnchor}
          orderId={order.id}
          orderName={order.name}
          slot={nowSlot}
          onClose={() => setPopoverAnchor(null)}
          sheetId={sheetId}
          gridKey={gridKey}
        />
      )}
      {isControlled && order.controlledSubstance && (
        <ControlledSubstanceAdminDialog
          open={csDialogOpen}
          onClose={() => setCsDialogOpen(false)}
          onSuccess={() => queryClient.invalidateQueries(['admin-grid', sheetId])}
          orderId={order.id}
          orderName={order.name}
          orderDose={order.dose}
          orderRoute={order.route}
          controlledSubstance={order.controlledSubstance}
          slot={nowSlot}
          currentUserId={currentUserId ?? ''}
        />
      )}
    </>
  );
};

export default TreatmentGrid;
